import { NSARouterMakerResult } from "namirasoft-account-react";
import { EnvService } from "namirasoft-core";
import { NSButton } from "namirasoft-site-react";
import { useEffect, useRef } from "react";
import { AccountRoutes } from "../AccountRoutes";

interface GithubLoginButtonProps extends NSARouterMakerResult
{
    clientId: string;
}

const POPUP_NAME = "namirasoft-github-oauth";
const POPUP_FEATURES = "popup=yes,width=520,height=640";
const STATE_STORAGE_KEY = "namirasoft-github-oauth-state";

function generateState(): string
{
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, "0")).join("");
}

function GithubLoginButton(props: GithubLoginButtonProps)
{
    const popupRef = useRef<Window | null>(null);
    const pollRef = useRef<number | null>(null);
    const expectedStateRef = useRef<string | null>(null);

    function cleanup()
    {
        if (pollRef.current !== null)
        {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }
        popupRef.current = null;
        expectedStateRef.current = null;
        try { window.sessionStorage.removeItem(STATE_STORAGE_KEY); } catch { }
    }

    function exchange(code: string)
    {
        props.account.server.user.Github({
            client_id: props.clientId,
            code,
            access_token: null,
        }).then(() =>
        {
            props.url.redirect(AccountRoutes.Home, {});
        }).catch(() => { });
    }

    useEffect(() =>
    {
        function onMessage(event: MessageEvent)
        {
            if (event.origin !== window.location.origin)
                return;
            const data = event.data as { source?: string; code?: string; state?: string; error?: string; error_description?: string } | null;
            if (!data || data.source !== "namirasoft-github-oauth")
                return;
            if (data.error)
            {
                props.notifier.onError(data.error_description ?? data.error);
                cleanup();
                return;
            }
            if (!data.code)
                return;
            if (expectedStateRef.current && data.state !== expectedStateRef.current)
            {
                props.notifier.onError("GitHub OAuth state mismatch.");
                cleanup();
                return;
            }
            cleanup();
            exchange(data.code);
        }
        window.addEventListener("message", onMessage);
        return () =>
        {
            window.removeEventListener("message", onMessage);
            cleanup();
        };
        // eslint-disable-next-line
    }, []);

    function login()
    {
        try
        {
            const state = generateState();
            expectedStateRef.current = state;
            try { window.sessionStorage.setItem(STATE_STORAGE_KEY, state); } catch { }

            let REACT_APP_BASE_PATH = new EnvService("REACT_APP_BASE_PATH", true).getString();
            const redirect_uri = `${window.location.origin}${REACT_APP_BASE_PATH}${AccountRoutes.GithubOauthCallback}`;
            const url = new URL("https://github.com/login/oauth/authorize");
            url.searchParams.set("client_id", props.clientId);
            url.searchParams.set("redirect_uri", redirect_uri);
            url.searchParams.set("scope", "read:user user:email");
            url.searchParams.set("state", state);

            const popup = window.open(url.toString(), POPUP_NAME, POPUP_FEATURES);
            if (!popup)
            {
                props.notifier.onError("Popup was blocked. Please allow popups and try again.");
                return;
            }
            popupRef.current = popup;
            pollRef.current = window.setInterval(() =>
            {
                if (popup.closed)
                    cleanup();
            }, 500);
        } catch (error)
        {
            props.notifier.onError(error as Error);
        }
    }

    return (
        <NSButton
            title="Sign in with GitHub"
            icon={{
                src: "https://static.namirasoft.com/image/application/github/logo/base.png",
            }}
            onClick={{
                action: () => login(),
                showLoading: false,
            }}
            style={{ border: "1px solid rgba(3, 119, 255, 1)" }}
        />
    );
}

export default GithubLoginButton;